import { useCallback, useEffect, useMemo, useRef, useState } from "react"
import { CurrencyHelper } from "~/helpers/currency_helper"
import {
  PillTabs,
  PillTabsList,
  PillTabsTrigger,
  PillTabsTriggerHint,
  PillTabsContent,
} from "~/react/ui/pill-tabs"
import { ResultType } from "../multi_domain_search/result"
import { departmentsList } from "~/react/components/search/filters"
import { DepartmentSection } from "~/react/components/search/department_section"
import { FilterEmptyState } from "~/react/components/search/filter-empty-state"
import { Alert, AlertIcon, AlertContent } from "~/react/ui/alert"
import { Button, ButtonSuffix } from "~/react/ui/button"
import { Icon } from "~/react/ui/icon"
import { useVirtualizer } from "@tanstack/react-virtual"
import { Result } from "~/react/components/multi_domain_search/result"
import { useSplittedResults } from "~/react/hooks/useSplittedResults"
import { type TabSelected } from "~/react/types/tabSelected"
import { LoadingSpinner } from "~/react/ui/loading-spinner"

export default function EmailsList({
  results,
  anyFiltersSet,
  selectedIds,
  campaigns,
  leadsLists,
  isLoading,
  showGenericAlert = true,

  handleReveal,
  handleRevealMultiple,
  handleLeadsListSave,
  handleCampaignsSave,
  handleSelect,
  onTabSelected,
  clearFilters,
}: {
  results: ResultType[]
  anyFiltersSet: boolean
  selectedIds: number[]
  campaigns: { id: number; name: string }[]
  leadsLists: { id: number; name: string }[]
  isLoading: boolean
  showGenericAlert: boolean

  handleReveal: (id: number) => void
  handleRevealMultiple: (ids: number[]) => void
  handleLeadsListSave: (listId: number) => void
  handleCampaignsSave: (campaignId: number) => void
  handleSelect: (ids: number[]) => void
  onTabSelected?: (tab: TabSelected) => void
  clearFilters: () => void
}) {
  const { allPeopleResults, decisionMakersResults, genericResults } =
    useSplittedResults(results)

  const [tabSelected, setTabSelected] = useState<TabSelected>("all_people")

  // If there are no "All People" results but generic results exist,
  // automatically switch to the "generic" tab so it's shown by default.
  useEffect(() => {
    if (
      tabSelected === "all_people" &&
      allPeopleResults.length === 0 &&
      genericResults.length > 0
    ) {
      setTabSelected("generic")
      onTabSelected?.("generic")
    }
  }, [
    tabSelected,
    allPeopleResults.length,
    genericResults.length,
    onTabSelected,
  ])

  // Group results by department for the "All People" tab
  const resultsByDepartment = useMemo(() => {
    const personalEmails = results.filter(
      (result) => result.type === "personal",
    )

    // Group by department
    const grouped = personalEmails.reduce(
      (acc, result) => {
        const dept = result.department || null
        if (!acc[dept]) acc[dept] = []
        acc[dept].push(result)
        return acc
      },
      {} as Record<string, ResultType[]>,
    )

    // Convert to array and sort by department name
    return Object.entries(grouped).sort(([deptA], [deptB]) => {
      if (deptA === null) return 1 // Unknown department goes last
      if (deptB === null) return -1
      return deptA.localeCompare(deptB)
    })
  }, [results])

  const decisionMakersListRef = useRef<HTMLDivElement>(null)
  const genericListRef = useRef<HTMLDivElement>(null)

  const decisionMakersListVirtualizer = useVirtualizer({
    count: decisionMakersResults.length,
    getScrollElement: () => decisionMakersListRef.current,
    estimateSize: useCallback(() => 80, []),
    enabled: tabSelected === "decision_makers",
  })
  const genericListVirtualizer = useVirtualizer({
    count: genericResults.length,
    getScrollElement: () => genericListRef.current,
    estimateSize: useCallback(() => 80, []),
    enabled: tabSelected === "generic",
  })

  return (
    <div className="ds-results__body mb-4" id="domain_search_emails">
      {/* Tab Headers */}
      <div className="my-4">
        <PillTabs value={tabSelected}>
          <PillTabsList size="md" fullWidth className="my-4">
            <PillTabsTrigger
              value="all_people"
              onClick={() => {
                setTabSelected("all_people")
                onTabSelected?.("all_people")
              }}
            >
              {i18n.t("js.search.tabs.all_people")}
              <PillTabsTriggerHint>
                · {CurrencyHelper.numberWithDelimiter(allPeopleResults.length)}
              </PillTabsTriggerHint>
            </PillTabsTrigger>
            <PillTabsTrigger
              value="decision_makers"
              onClick={() => {
                setTabSelected("decision_makers")
                onTabSelected?.("decision_makers")
              }}
            >
              {i18n.t("js.search.tabs.decision_makers")}
              <PillTabsTriggerHint>
                ·{" "}
                {CurrencyHelper.numberWithDelimiter(
                  decisionMakersResults.length,
                )}
              </PillTabsTriggerHint>
            </PillTabsTrigger>
            <PillTabsTrigger
              value="generic"
              onClick={() => {
                setTabSelected("generic")
                onTabSelected?.("generic")
              }}
            >
              {i18n.t("js.search.tabs.generic")}
              <PillTabsTriggerHint>
                · {CurrencyHelper.numberWithDelimiter(genericResults.length)}
              </PillTabsTriggerHint>
            </PillTabsTrigger>
          </PillTabsList>
          <PillTabsContent value="all_people">
            {Object.entries(departmentsList).map(([key, value]) => (
              <DepartmentSection
                key={key}
                department={value}
                title={value}
                hideCompanyDetails={true}
                results={
                  resultsByDepartment.find(
                    ([department, departmentResults]) => department === key,
                  )?.[1] || []
                }
                selectedIds={selectedIds}
                onSelect={handleSelect}
                handleReveal={handleReveal}
                handleLeadsListSave={handleLeadsListSave}
                handleCampaignsSave={handleCampaignsSave}
                leadsLists={leadsLists}
                campaigns={campaigns}
                defaultExpanded={Object.entries(departmentsList)[0][0] === key}
              />
            ))}
            <DepartmentSection
              key="unknown"
              department="Department unknown"
              title="Department unknown"
              hideCompanyDetails={true}
              results={
                resultsByDepartment.find(
                  ([department, departmentResults]) => department === "null",
                )?.[1] || []
              }
              selectedIds={selectedIds}
              onSelect={handleSelect}
              handleReveal={handleReveal}
              handleLeadsListSave={handleLeadsListSave}
              handleCampaignsSave={handleCampaignsSave}
              leadsLists={leadsLists}
              campaigns={campaigns}
              defaultExpanded={resultsByDepartment[0]?.[0] === "null"}
            />

            {isLoading && <LoadingSpinner />}
            {allPeopleResults.length === 0 &&
              !isLoading &&
              (anyFiltersSet ? (
                <FilterEmptyState clearFilters={clearFilters} />
              ) : (
                <FilterEmptyState />
              ))}
          </PillTabsContent>
          <PillTabsContent
            value="decision_makers"
            ref={decisionMakersListRef}
            style={{ height: "600px", overflow: "auto" }}
          >
            <div
              style={
                decisionMakersResults.length > 0
                  ? {
                      height: `${decisionMakersListVirtualizer.getTotalSize()}px`,
                      position: "relative",
                    }
                  : undefined
              }
            >
              {decisionMakersListVirtualizer
                .getVirtualItems()
                .map((virtualItem) => (
                  <div
                    key={virtualItem.key}
                    data-index={virtualItem.index}
                    ref={decisionMakersListVirtualizer.measureElement}
                    style={{
                      position: "absolute",
                      top: 0,
                      left: 0,
                      width: "100%",
                      transform: `translateY(${virtualItem.start}px)`,
                    }}
                  >
                    <Result
                      result={decisionMakersResults[virtualItem.index]}
                      selected={selectedIds.includes(
                        decisionMakersResults[virtualItem.index]?.id,
                      )}
                      hideCompanyDetails={true}
                      leadsLists={leadsLists}
                      campaigns={campaigns}
                      onSelect={handleSelect}
                      handleReveal={handleReveal}
                      handleLeadsListSave={handleLeadsListSave}
                      handleCampaignsSave={handleCampaignsSave}
                    />
                  </div>
                ))}
              {isLoading && <LoadingSpinner />}
              {decisionMakersResults.length === 0 &&
                !isLoading &&
                (anyFiltersSet ? (
                  <FilterEmptyState clearFilters={clearFilters} />
                ) : (
                  <FilterEmptyState />
                ))}
            </div>
          </PillTabsContent>

          <PillTabsContent
            value="generic"
            ref={genericListRef}
            style={{ height: "600px", overflow: "auto" }}
          >
            {showGenericAlert && (
              <Alert
                variant="info"
                size="small"
                className="mb-2 align-items-center"
              >
                <AlertIcon>
                  <span className="h-alert__icon far fa-info-circle"></span>
                </AlertIcon>
                <AlertContent className="text-xs">
                  {i18n.t("js.search.generic_email_badge.description", {
                    pretty_count: CurrencyHelper.numberWithDelimiter(
                      genericResults.length,
                    ),
                  })}
                </AlertContent>
                <Button
                  size="sm"
                  onClick={() => handleReveal(genericResults[0].id)}
                >
                  {i18n.t("js.search.generic_email_badge.cta")}
                  <ButtonSuffix>
                    <span className="cost-indicator">
                      <Icon
                        icon="coin"
                        size="xs"
                        className="cost-indicator__icon"
                      />
                      1
                    </span>
                  </ButtonSuffix>
                </Button>
              </Alert>
            )}
            <div
              style={
                genericResults.length > 0
                  ? {
                      height: `${genericListVirtualizer.getTotalSize()}px`,
                      position: "relative",
                    }
                  : undefined
              }
            >
              {genericListVirtualizer.getVirtualItems().map((virtualItem) => (
                <div
                  key={virtualItem.key}
                  data-index={virtualItem.index}
                  ref={genericListVirtualizer.measureElement}
                  style={{
                    position: "absolute",
                    top: 0,
                    left: 0,
                    width: "100%",
                    transform: `translateY(${virtualItem.start}px)`,
                  }}
                >
                  <Result
                    result={genericResults[virtualItem.index]}
                    hideCompanyDetails={true}
                    selected={selectedIds.includes(
                      genericResults[virtualItem.index]?.id,
                    )}
                    leadsLists={leadsLists}
                    campaigns={campaigns}
                    onSelect={handleSelect}
                    handleReveal={handleReveal}
                    handleLeadsListSave={handleLeadsListSave}
                    handleCampaignsSave={handleCampaignsSave}
                  />
                </div>
              ))}
              {isLoading && <LoadingSpinner />}
              {genericResults.length === 0 &&
                !isLoading &&
                (anyFiltersSet ? (
                  <FilterEmptyState clearFilters={clearFilters} />
                ) : (
                  <FilterEmptyState />
                ))}
            </div>
          </PillTabsContent>
        </PillTabs>
      </div>
    </div>
  )
}
